import { useEffect } from "react";
import { leadAllianceTracking } from "utils/ad-network";
import { getCookie } from "utils/cookie";
import { getLeadAllianceProgramId, getOrg, Org } from "utils/site";
import { inProduction } from "../../../../../config";
import { SHA256 } from "utils/utils";
import { Animal } from "components/Signup/store/state/types";
import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";

const INSTANCE_URL = "https://eur.partner.lassie.co/tl.php";

const affiliateCodes: Record<Org, string[]> = {
  ger: ["SCHLITZOHR15", "SH15"],
  swe: [],
  fra: [],
};

const getTrackingPixel = async ({
  policyNumber,
  programId,
  lea_source_pc,
  discountCode,
  pid,
}: {
  policyNumber: string;
  programId: string;
  lea_source_pc: string | null;
  discountCode: string | null;
  pid: string;
}) => {
  const img = document.createElement("img");

  const qp = new URLSearchParams();

  return SHA256(policyNumber).then((hashedId) => {
    qp.set("p", programId);
    qp.set("oid", hashedId);
    qp.set("lea_source_pc", lea_source_pc ?? "");
    qp.set("voucher", discountCode ?? "");
    qp.set("ovalue", "0");
    qp.set("ocategory", pid);

    img.src = `${INSTANCE_URL}?${qp.toString()}`;
    img.alt = "";
    img.width = 0;
    img.height = 0;

    return img;
  });
};

export const useLeadAllianceTracking = ({
  purchaseCompleted,
  animals,
  productId,
}: {
  animals: Animal[];
  purchaseCompleted: boolean;
  productId?: string;
}) => {
  useEffect(() => {
    const imgs: HTMLImageElement[] = [];

    animals.forEach((animal) => {
      animal.insurances.forEach(async (insurance) => {
        if (!insurance.policy?.policyNumber) {
          return;
        }

        const programId = getLeadAllianceProgramId();
        const lea_source_pc = getCookie(leadAllianceTracking.cookieName);
        const pid =
          productId ??
          `${animal.species}-${
            insurance.product === SignUpProductEnum.liability
              ? "liability"
              : `health${insurance.limit === 0 ? "-op" : ""}`
          }`;

        if (!purchaseCompleted && programId && inProduction) {
          // If referral discount code is used
          if (
            insurance.discountCode &&
            affiliateCodes[getOrg()].includes(insurance.discountCode ?? "")
          ) {
            const trackingPixel = await getTrackingPixel({
              policyNumber: insurance.policy.policyNumber,
              discountCode: insurance.discountCode,
              lea_source_pc,
              programId,
              pid,
            });

            if (trackingPixel) {
              imgs.push(trackingPixel);
            }

            return;
          }

          // If referral cookies is set
          if (lea_source_pc) {
            const trackingPixel = await getTrackingPixel({
              policyNumber: insurance.policy.policyNumber,
              discountCode: insurance.discountCode,
              lea_source_pc,
              programId,
              pid,
            });

            if (trackingPixel) {
              imgs.push(trackingPixel);
            }
          }

          return;
        }

        imgs.forEach((img) => document.body.appendChild(img));
      });
    });

    return () => {
      imgs.forEach((img) => document.body.removeChild(img));
    };
  }, [animals, productId, purchaseCompleted]);
};
